import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  TextField,
} from '@mui/material';
import { GameTypeName, PremiumGame } from '@loyalty/shared-common/premium-games-api-client';
import { colorPalette } from '@qualifio/ui-commons';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePremiumGames } from '../../../hooks/use-premium-games';

const FIELD_ID = 'advanced-format-game-config';
const THUMBNAIL_WIDTH = 48;
const THUMBNAIL_HEIGHT = 32;

function Thumbnail({ screenshot }: { screenshot?: string }) {
  return (
    <Box
      sx={{
        width: THUMBNAIL_WIDTH,
        height: THUMBNAIL_HEIGHT,
        flexShrink: 0,
        borderRadius: 0.5,
        backgroundColor: colorPalette.grey.light,
        backgroundImage: screenshot ? `url(${screenshot})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    />
  );
}

interface PremiumGameFieldProps {
  label: string;
  gameType?: GameTypeName;
  value?: string;
  onChange: (uuid?: string) => void;
  errorMessage?: string;
}

/**
 * Picks a premium game for the advanced format widget. Only the game uuid is stored, which is the
 * config id the game application is opened with.
 */
export function PremiumGameField({
  label,
  gameType,
  value,
  onChange,
  errorMessage,
}: PremiumGameFieldProps) {
  const { t } = useTranslation();
  const hasGameType = !!gameType;
  const { data: games = [], isLoading, isError, isFetched } = usePremiumGames(gameType);

  /*
   * A widget can reference a game that is not in the list anymore 
  (deleted or not created yet on account because of legacy implementation). 
  Keeping it as an option avoids silently dropping a working configuration 
  (we display uuid instead of name + warning message).
   */
  const isMissingFromList = !!value && isFetched && !games.some(game => game.uuid === value);

  const options = useMemo<PremiumGame[]>(
    () => (isMissingFromList ? [...games, { uuid: value, name: value } as PremiumGame] : games),
    [games, value, isMissingFromList],
  );

  const selectedGame = options.find(game => game.uuid === value) ?? null;
  const errorText = errorMessage || (isError ? t('editor.advanced-format.traits.game.error') : '');

  return (
    <FormControl error={!!errorText} fullWidth>
      <InputLabel
        id={`${FIELD_ID}-label`}
        htmlFor={FIELD_ID}
        sx={theme => ({
          color: `${colorPalette.grey.dark} !important`,
          marginBottom: theme.spacing(0.4),
          transform: 'none',
          position: 'relative',
          userSelect: 'inherit',
        })}
      >
        {label}
      </InputLabel>
      <Autocomplete<PremiumGame>
        id={FIELD_ID}
        options={options}
        value={selectedGame}
        disabled={!hasGameType}
        loading={isLoading}
        disableCloseOnSelect={false}
        loadingText={t('editor.advanced-format.traits.game.loading')}
        noOptionsText={t('editor.advanced-format.traits.game.empty')}
        getOptionLabel={game => game.name}
        isOptionEqualToValue={(option, selected) => option.uuid === selected.uuid}
        onChange={(_event, game) => onChange(game?.uuid)}
        renderOption={(props, game) => (
          <Box component="li" {...props} key={game.uuid} sx={{ gap: 1, alignItems: 'center' }}>
            <Thumbnail screenshot={game.screenshot} />
            <Box sx={{ overflowWrap: 'anywhere' }}>{game.name}</Box>
          </Box>
        )}
        renderInput={({ InputLabelProps, ...params }) => (
          // InputLabelProps is dropped on purpose: the label is rendered above the field
          <TextField
            {...params}
            placeholder={t('editor.advanced-format.traits.game.placeholder')}
            InputProps={{
              ...params.InputProps,
              startAdornment:
                selectedGame && !isMissingFromList ? (
                  <Thumbnail screenshot={selectedGame.screenshot} />
                ) : undefined,
              endAdornment: (
                <>
                  {isLoading && <CircularProgress size={16} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {(errorText || isMissingFromList) && (
        <FormHelperText error={!!errorText}>
          {errorText || t('editor.advanced-format.traits.game.unavailable')}
        </FormHelperText>
      )}
    </FormControl>
  );
}
